import { useIntake, IntakeShell } from "./IntakeShell";
import { CTA_WARRANTY } from "../../lib/ctaVocabulary";

/**
 * Lightweight warranty intake.
 *
 * Iter-196.PP — Form-friction simplification (2026-02-16). Reduced
 * from 4 fields → 2 fields. Affiliate partners re-ask model + mileage
 * on their own intake screens, so collecting them twice was friction
 * without conversion lift. Year + Make is the minimum the partner
 * needs to route to the right provider tier (luxury vs. mainstream)
 * and quote a starting price band.
 *
 * Retired in iter-196.PP:
 *   - vehicle_model (free-text input)
 *   - vehicle_mileage (range slider)
 *
 * No ZIP — warranty pricing is national, not regional. We seed a
 * "00000" sentinel so the backend's `zip_code` schema requirement
 * passes; iter-150 analytics filters strip 00000 from regional
 * rollups.
 */
const cur = new Date().getFullYear();

const DEFAULTS = {
  vehicle_year: cur - 4,
  vehicle_make: "",
  // Backend requires a `zip_code` field on every quote-requests POST
  // (min_length 3 — see backend/models/__init__.py). Warranty pricing is
  // national, not regional, so we seed a sentinel value ("00000") rather
  // than asking the user for one. Downstream analytics filters this out.
  zip_code: "00000",
};

// Year range — 20 years back covers most VSC eligibility windows; +1
// allows next-model-year buyers to pre-shop.
const YEAR_OPTIONS = Array.from(
  { length: 22 },
  (_, i) => cur + 1 - i,
);

const POPULAR_MAKES = [
  "Toyota",
  "Honda",
  "Ford",
  "Chevrolet",
  "Hyundai",
  "Kia",
  "BMW",
  "Mercedes-Benz",
  "Audi",
  "Lexus",
  "Subaru",
  "Mazda",
  "Nissan",
  "Volkswagen",
  "Tesla",
  "Other",
];

function validate(values) {
  if (
    !values.vehicle_year ||
    values.vehicle_year < cur - 25 ||
    values.vehicle_year > cur + 1
  )
    return `Year must be between ${cur - 25} and ${cur + 1}`;
  if (!values.vehicle_make) return "Pick a vehicle make";
  return null;
}

export default function IntakeWarranty() {
  const state = useIntake({
    category: "warranty",
    defaults: DEFAULTS,
    validate,
    contextLine:
      "Compare vehicle protection plans based on your vehicle, mileage, and ownership goals.",
  });
  const { values, setField } = state;

  return (
    <IntakeShell
      category="warranty"
      state={state}
      submitLabel={CTA_WARRANTY}
    >
      <Field
        label="Vehicle year"
        helper="Used to size factory-warranty overlap + claim age limits."
      >
        <select
          value={values.vehicle_year}
          onChange={(e) => setField("vehicle_year", +e.target.value)}
          data-testid="intake-warranty-year"
          className="w-full h-12 px-3 bg-white border border-slate-300 focus:border-slate-950 outline-none text-sm font-semibold text-slate-950"
        >
          {YEAR_OPTIONS.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      </Field>

      <Field
        label="Make"
        helper="Determines provider eligibility — luxury makes price differently."
      >
        <select
          value={values.vehicle_make}
          onChange={(e) => setField("vehicle_make", e.target.value)}
          data-testid="intake-warranty-make"
          className="w-full h-12 px-3 bg-white border border-slate-300 focus:border-slate-950 outline-none text-sm font-semibold text-slate-950"
        >
          <option value="" disabled>
            Pick a make
          </option>
          {POPULAR_MAKES.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
      </Field>
    </IntakeShell>
  );
}

function Field({ label, helper, children }) {
  // Iter-275.AC (Phase B-2) — Wrap `{children}` inside the `<label>` so the
  // `<select>` becomes implicitly associated with its label. Previously
  // the `<label>` was a sibling with no `htmlFor`, leaving the dropdown
  // unlabelled to AT and breaking click-to-focus-from-label behaviour.
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-2 block">
        {label}
      </span>
      {children}
      {helper && (
        <p className="mt-1.5 text-[11px] text-slate-500 leading-snug">
          {helper}
        </p>
      )}
    </label>
  );
}
